import React, { useState, useEffect } from 'react';
import { useNavigation } from '../../contexts/NavigationContext';
import Icon from '../AppIcon';
import Button from '../ui/Button';

const NavigationSettings = ({ isOpen, onClose }) => {
  const {
    navigationPreferences,
    updateNavigationPreferences
  } = useNavigation();

  const [localPreferences, setLocalPreferences] = useState(navigationPreferences);

  // Sync local state when context preferences change (e.g. loaded from localStorage)
  useEffect(() => {
    setLocalPreferences(navigationPreferences);
  }, [isOpen]); // re-sync each time the modal opens
  const [activeTab, setActiveTab] = useState('general');

  const handlePreferenceChange = (key, value) => {
    setLocalPreferences(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSave = () => {
    updateNavigationPreferences(localPreferences);
    onClose();
  };

  const handleReset = () => {
    const defaultPreferences = {
      showBreadcrumbs: true,
      showQuickActions: true,
      compactMode: false,
      sidebarCollapsed: false
    };
    setLocalPreferences(defaultPreferences);
    updateNavigationPreferences(defaultPreferences);
  };

  const tabs = [
    { id: 'general', label: 'General', icon: 'Settings' },
    { id: 'keyboard', label: 'Keyboard', icon: 'Keyboard' }
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Navigation Settings</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg "
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="flex">
          {/* Sidebar Tabs */}
          <div className="w-48 bg-gray-50 border-r border-gray-200">
            <div className="p-4 space-y-1">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center space-x-3 px-3 py-2 text-sm rounded-lg  ${
                    activeTab === tab.id
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                >
                  <Icon name={tab.icon} size={16} />
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 p-6 overflow-y-auto max-h-[70vh]">
            {activeTab === 'general' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">General Preferences</h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <label className="text-sm font-medium text-gray-700">Show Breadcrumbs</label>
                        <p className="text-xs text-gray-500">Display navigation breadcrumbs at the top of pages</p>
                      </div>
                      <button
                        onClick={() => handlePreferenceChange('showBreadcrumbs', !localPreferences.showBreadcrumbs)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full  ${
                          localPreferences.showBreadcrumbs ? 'bg-blue-600' : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            localPreferences.showBreadcrumbs ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <label className="text-sm font-medium text-gray-700">Show Quick Actions</label>
                        <p className="text-xs text-gray-500">Display quick action buttons in the navigation</p>
                      </div>
                      <button
                        onClick={() => handlePreferenceChange('showQuickActions', !localPreferences.showQuickActions)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full  ${
                          localPreferences.showQuickActions ? 'bg-blue-600' : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            localPreferences.showQuickActions ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>

                  </div>
                </div>
              </div>
            )}

            {activeTab === 'keyboard' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Keyboard Shortcuts</h3>
                  <p className="text-sm text-gray-600 mb-6">
                    Use these keyboard shortcuts to navigate quickly through the application.
                  </p>

                  <div className="space-y-4">
                    <div className="grid grid-cols-1 gap-3">
                      {[
                        { key: 'Alt + H', description: 'Go to Dashboard' },
                        { key: 'Alt + S', description: 'Go to Search/Jobs' },
                        { key: 'Alt + M', description: 'Go to Messages' },
                        { key: 'Alt + P', description: 'Go to Profile' },
                        { key: 'Alt + A', description: 'Go to Analytics' },
                        { key: 'Alt + J', description: 'Go to Jobs' },
                        { key: 'Alt + 1-9', description: 'Quick Access Items (by position)' },
                        { key: 'Alt + Backspace', description: 'Go Back' },
                        { key: '/', description: 'Focus Search Input' },
                        { key: 'Escape', description: 'Close Modals/Dropdowns' },
                        { key: '?', description: 'Show Keyboard Shortcuts Help' }
                      ].map((shortcut, index) => (
                        <div key={index} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                          <span className="text-sm text-gray-700">{shortcut.description}</span>
                          <kbd className="px-2 py-1 text-xs font-semibold text-gray-800 bg-white border border-gray-300 rounded shadow-sm">
                            {shortcut.key}
                          </kbd>
                        </div>
                      ))}
                    </div>

                    <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex items-center space-x-2 mb-2">
                        <Icon name="Info" size={16} className="text-blue-600" />
                        <span className="text-sm font-medium text-blue-800">Pro Tip</span>
                      </div>
                      <p className="text-sm text-blue-700">
                        Keyboard shortcuts work from anywhere in the application, except when typing in input fields. 
                        Press <kbd className="px-1 py-0.5 text-xs bg-white border rounded">?</kbd> anytime to see this help.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={handleReset}
            className="text-sm text-gray-600 hover:text-gray-900 "
          >
            Reset to Defaults
          </button>
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
            >
              Save Changes
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NavigationSettings;
